/**
 * Money is always an integer number of centavos (BRL). Floats never carry money.
 * JS numbers are exact for integers up to 2^53 (≈ R$ 90 trilhões), far above any amount here;
 * every operation re-checks that bound instead of silently losing precision.
 */

export type Cents = number;

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

export function isCents(value: unknown): value is Cents {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

export function assertCents(value: unknown, label = 'valor'): Cents {
  if (!isCents(value))
    throw new MoneyError(`${label}: esperado inteiro em centavos, recebido ${String(value)}`);
  return value;
}

export function addCents(a: Cents, b: Cents): Cents {
  return assertCents(assertCents(a) + assertCents(b), 'soma');
}

export function sumCents(values: Iterable<Cents | null | undefined>): Cents {
  let total = 0;
  for (const v of values) {
    if (v === null || v === undefined) continue;
    total = addCents(total, v);
  }
  return total;
}

/**
 * Parses a decimal string with an explicit decimal separator (the import layout declares it, we never guess).
 * Accepts optional "R$", spaces, thousands separators and a leading/trailing minus or parentheses.
 * More than 2 decimal places is rejected: statement values with sub-centavo precision are a data problem,
 * not something to round silently.
 */
export function parseDecimalToCents(input: string, decimalSeparator: ',' | '.'): Cents {
  const raw = input.trim();
  if (raw === '') throw new MoneyError('valor vazio');
  let s = raw.replace(/^R\$\s*/i, '').replace(/\s+/g, '');
  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1);
  }
  if (s.startsWith('-')) {
    negative = !negative;
    s = s.slice(1);
  } else if (s.endsWith('-')) {
    negative = !negative;
    s = s.slice(0, -1);
  } else if (s.startsWith('+')) {
    s = s.slice(1);
  }
  const thousands = decimalSeparator === ',' ? '.' : ',';
  const [intPartRaw, fracPart, ...rest] = s.split(decimalSeparator);
  if (rest.length > 0 || intPartRaw === undefined) throw new MoneyError(`valor inválido: "${raw}"`);
  const groupedOk = new RegExp(`^\\d{1,3}(\\${thousands}\\d{3})+$`).test(intPartRaw);
  if (intPartRaw.includes(thousands) && !groupedOk)
    throw new MoneyError(`separador de milhar inválido: "${raw}"`);
  const intPart = intPartRaw.split(thousands).join('');
  if (!/^\d+$/.test(intPart)) throw new MoneyError(`valor inválido: "${raw}"`);
  const frac = fracPart ?? '';
  if (!/^\d{0,2}$/.test(frac)) {
    throw new MoneyError(`valor com mais de 2 casas decimais ou inválido: "${raw}"`);
  }
  const cents = BigInt(intPart) * 100n + BigInt(frac.padEnd(2, '0') || '0');
  const result = Number(negative ? -cents : cents);
  return assertCents(result, `valor "${raw}"`);
}

/**
 * Converts a spreadsheet numeric cell (IEEE double) to centavos. The double is first printed with 15
 * significant digits — the precision Excel itself displays — which turns 1234.5599999999 back into
 * "1234.56". Anything that still has more than 2 decimals is rejected.
 */
export function spreadsheetNumberToCents(value: number): Cents {
  if (!Number.isFinite(value)) throw new MoneyError(`número inválido: ${value}`);
  const text = Number(value.toPrecision(15)).toString();
  if (/e/i.test(text)) throw new MoneyError(`número fora da faixa: ${value}`);
  return parseDecimalToCents(text, '.');
}

/** Integer division with round-half-even (ties to the even digit), as in ABNT NBR 5891 — to be confirmed by accounting. */
export function divRoundHalfEven(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new MoneyError('divisão por zero');
  const negative = numerator < 0n !== denominator < 0n;
  const n = numerator < 0n ? -numerator : numerator;
  const d = denominator < 0n ? -denominator : denominator;
  let q = n / d;
  const r = n % d;
  const twice = r * 2n;
  if (twice > d || (twice === d && q % 2n === 1n)) q += 1n;
  return negative ? -q : q;
}

/** amount × numerator / denominator, rounded half-even to centavos (e.g. deductions by basis points). */
export function mulDivCents(amount: Cents, numerator: number, denominator: number): Cents {
  assertCents(amount);
  if (!Number.isSafeInteger(numerator) || !Number.isSafeInteger(denominator)) {
    throw new MoneyError('fator deve ser inteiro');
  }
  return assertCents(Number(divRoundHalfEven(BigInt(amount) * BigInt(numerator), BigInt(denominator))));
}

/** Formats centavos as "R$ 1.234,56" using integer arithmetic only. */
export function formatBRL(cents: Cents): string {
  assertCents(cents);
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const reais = Math.trunc(abs / 100);
  const cent = abs % 100;
  const grouped = reais.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${negative ? '-' : ''}R$ ${grouped},${cent.toString().padStart(2, '0')}`;
}

/** Plain decimal string "1234.56" for CSV/JSON exports. */
export function centsToDecimalString(cents: Cents): string {
  assertCents(cents);
  const negative = cents < 0;
  const abs = Math.abs(cents);
  return `${negative ? '-' : ''}${Math.trunc(abs / 100)}.${(abs % 100).toString().padStart(2, '0')}`;
}
